import React from 'react';
import { connect } from 'react-redux';
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  SearchBox,
  Skeleton,
  SkeletonItem,
  Spinner,
  TableColumnDefinition,
  Toast,
  ToastTitle,
  Toaster,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  ToolbarRadioButton,
  ToolbarRadioGroup,
  Tooltip,
  createTableColumn,
  mergeClasses,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import {
  ArrowClockwise20Regular,
  ArrowDownload20Regular,
  ArrowUpload20Regular,
  Delete20Regular,
  FolderAdd20Regular,
  LockClosed16Regular,
  Rename20Regular,
  ArrowUp20Regular,
  Dismiss20Regular,
  Document24Regular,
  Eye20Regular,
  DocumentPdf24Regular,
  DocumentTable24Regular,
  DocumentText24Regular,
  Folder24Regular,
  FolderOpen24Regular,
  Grid20Regular,
  Image24Regular,
  Open20Regular,
  TextBulletListLtr20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import * as ComponentFunctions from './FilesExplorer.Functions';
import { useFilesExplorerStyles } from './FilesExplorer.Styles';
import { formatFileSize } from '../../../functions/AzureFileUpload.Functions';
import {
  collectStorageEntityRefs,
  getOpenFileAction,
  getParentPath,
  Translate,
} from '../../../functions/StorageBrowser.Functions';
import FilePreview from '../FilePreview/FilePreview.Component';
import { PreviewFile } from '../FilePreview/FilePreview.Functions';
import { StorageFileAction } from '../../../models/Entities/AzureFiles/StorageBrowser';
import { useFileDropTarget } from '../../Commons/FileDropZone/useFileDropTarget';
import { FolderNameDialog } from '../StorageDialogs/FolderNameDialog.Component';
import { ConfirmActionDialog } from '../StorageDialogs/ConfirmActionDialog.Component';
import { ExplorerUploadBatch, ExplorerUploadPanel } from '../ExplorerUploadPanel/ExplorerUploadPanel.Component';
import {
  buildFolderAcceptAttribute,
  describeStorageError,
  getPathAfterRename,
  resolveBlobRegisterTarget,
  resolveFolderCapabilities,
  toTenantRelativePath,
} from '../../../functions/StorageManagement.Functions';
import { ErrorType } from '../../../models/HttpError';

type ExplorerItem = ComponentFunctions.ExplorerItem;

const VIEW_MODE_STORAGE_KEY = 'prebiller.filesExplorer.viewMode';

const readViewMode = (): ComponentFunctions.ExplorerViewMode => {
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
};

const saveViewMode = (mode: ComponentFunctions.ExplorerViewMode) => {
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // per-viewer convenience only
  }
};

/** The target of the key event is an inner control (button / link) that already handles Enter. */
const isInnerControl = (event: React.KeyboardEvent<HTMLElement>): boolean => {
  const target = event.target as HTMLElement;
  return target !== event.currentTarget && !!target.closest('button, a, input');
};

export const FilesExplorerComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const translate = t as unknown as Translate;
  const styles = useFilesExplorerStyles();

  const [search, setSearch] = React.useState('');
  const [viewMode, setViewMode] = React.useState<ComponentFunctions.ExplorerViewMode>(readViewMode);
  const [previewFile, setPreviewFile] = React.useState<PreviewFile | undefined>(undefined);

  const { path, onNavigate, BrowseStorage, ResolveEntityNames } = props;

  // Every folder change (menu, breadcrumb, double click, deep link, back button) reloads the listing.
  React.useEffect(() => {
    setSearch('');
    BrowseStorage(path);
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  // Protocols / settlements / providers / sponsors shown by id get their name ("Protocolo 31 - PRESTO").
  React.useEffect(() => {
    const refs = collectStorageEntityRefs([path, ...props.folders.map((folder) => folder.path)]);
    if (refs.length > 0) ResolveEntityNames?.(refs);
  }, [path, props.folders]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = React.useMemo(
    () => ComponentFunctions.buildExplorerItems(props.folders, props.files, translate, props.ownerNames),
    [props.folders, props.files, props.ownerNames, t] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visibleItems = React.useMemo(() => ComponentFunctions.filterExplorerItems(items, search), [items, search]);

  const rootLabel = t('filesExplorer.root');
  const breadcrumbs = ComponentFunctions.buildExplorerBreadcrumbs(
    props.currentPath === path ? props.breadcrumbs : [],
    path,
    rootLabel,
    translate,
    props.ownerNames
  );
  const currentFolder = breadcrumbs[breadcrumbs.length - 1];

  const isBusy = props.isLoading || props.currentPath !== path;
  const folderCount = props.folders.length;
  const fileCount = props.files.length;

  const reload = () => BrowseStorage(path);

  //#region management (contract §9.4): upload, new folder, rename, delete

  const toasterId = useId('files-explorer-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const notify = (message: string, intent: 'success' | 'error' = 'success') =>
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent, timeout: 5000 }
    );

  const capabilities = resolveFolderCapabilities(props.currentPath === path ? props.currentFolder : null, path);
  const canManage = !isBusy && !props.error;
  const canUpload = canManage && capabilities.canUpload;
  const canCreateFolder = canManage && capabilities.canCreateFolder;
  const canRenameCurrent = canManage && !!path && !capabilities.isSystem;

  const [folderDialog, setFolderDialog] = React.useState<
    { mode: 'create' } | { mode: 'rename'; path: string; name: string; displayName: string } | undefined
  >(undefined);
  const [confirmDelete, setConfirmDelete] = React.useState<ExplorerItem | undefined>(undefined);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [uploadBatch, setUploadBatch] = React.useState<ExplorerUploadBatch | undefined>(undefined);
  const uploadInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadFolder = React.useRef(path);

  const startUpload = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      uploadFolder.current = path;
      setUploadBatch((current) => ({ id: (current?.id ?? 0) + 1, files }));
      setUploadOpen(true);
    },
    [path]
  );

  const { isDraggingFiles, dropTargetProps } = useFileDropTarget({ enabled: canUpload, onFilesDropped: startUpload });

  const onUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    startUpload(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const uploadFile = React.useCallback(
    (file: File, onProgress: (percent: number) => void) => props.UploadToFolder(uploadFolder.current, file, onProgress),
    [props.UploadToFolder] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Auto-register (spec 005, US7): a file uploaded into an entity folder is registered in its owner service
  // right away (the storage event is unreliable). The target comes from the blob path itself, so a retry
  // always registers in the folder where the file really went.
  const isRegistrable = React.useCallback((blobPath: string) => !!resolveBlobRegisterTarget(blobPath), []);
  const registerUploadedFile = React.useCallback(
    async (blobPath: string) => {
      const register = resolveBlobRegisterTarget(blobPath);
      if (!register) return;
      await props.RegisterAzureFile(register.kind, register.target, toTenantRelativePath(blobPath));
    },
    [props.RegisterAzureFile] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onUploaded = React.useCallback(
    (count: number) => {
      notify(t('filesExplorer.upload.uploadedToast', { count }));
      if (uploadFolder.current === path) BrowseStorage(path);
    },
    [path, BrowseStorage, t] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const siblingFolderNames = props.folders.map((folder) => folder.name);

  const describeFolderError = (error: ErrorType): string =>
    describeStorageError(
      error,
      {
        401: t('filesExplorer.messages.notAuthorized'),
        403: t('filesExplorer.messages.notAuthorized'),
        404: t('filesExplorer.messages.folderNotFound'),
        409: t('filesExplorer.folderValidation.exists'),
      },
      t('filesExplorer.messages.operationError')
    );

  const submitFolderDialog = async (name: string): Promise<string | void> => {
    if (!folderDialog) return;
    if (folderDialog.mode === 'create') {
      await props.CreateFolder(path, name);
      notify(t('filesExplorer.messages.folderCreated', { name }));
      BrowseStorage(path);
      return;
    }
    const result = await props.RenameFolder(folderDialog.path, name);
    const nextPath = getPathAfterRename(path, result.oldPath, result.newPath);
    if (nextPath !== undefined) onNavigate(nextPath, { replace: true });
    else BrowseStorage(path);
    return t('filesExplorer.messages.folderRenamed', { name, count: result.movedFiles });
  };

  const describeDeleteError = (error: ErrorType): string =>
    describeStorageError(
      error,
      {
        401: t('filesExplorer.messages.notAuthorized'),
        403: t('filesExplorer.messages.notAuthorized'),
        404:
          confirmDelete?.kind === 'folder'
            ? t('filesExplorer.messages.folderNotFound')
            : t('filesExplorer.messages.fileNotFound'),
        409: t('filesExplorer.messages.folderNotEmpty'),
      },
      t('filesExplorer.messages.operationError')
    );

  const confirmDeleteItem = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.kind === 'folder') await props.DeleteFolder(confirmDelete.path);
    else await props.DeleteFile(confirmDelete.path);
    notify(t('filesExplorer.messages.deleted', { name: confirmDelete.displayName }));
    setConfirmDelete(undefined);
    BrowseStorage(path);
  };

  const openRename = (target: { path: string; name: string; displayName: string }) =>
    setFolderDialog({ mode: 'rename', ...target });

  //#endregion

  const loadMore = () => {
    if (props.continuationToken) BrowseStorage(path, props.continuationToken);
  };
  const retry = () => {
    if (props.continuationToken && items.length > 0) loadMore();
    else reload();
  };

  const runFileAction = async (item: ExplorerItem, action: StorageFileAction) => {
    if (props.pendingFile) return;
    // The tab is opened inside the click so the popup blocker lets it through.
    const tab = action === 'open' ? ComponentFunctions.openPendingTab() : null;
    try {
      const sas = await props.RequestReadSas(item.path, action);
      if (action === 'open') ComponentFunctions.showInTab(tab, sas.sasUrl);
      else ComponentFunctions.triggerDownload(sas.sasUrl, item.name);
    } catch {
      ComponentFunctions.closePendingTab(tab);
    }
  };

  const previewItem = (item: ExplorerItem) =>
    setPreviewFile({
      path: item.path,
      name: item.name,
      extension: item.extension,
      size: item.size,
      lastModified: item.lastModified,
    });

  /** Click / double click / Enter: folders are opened, files are previewed. */
  const openItem = (item: ExplorerItem) => {
    if (item.kind === 'folder') onNavigate(item.path);
    else previewItem(item);
  };

  const onItemKeyDown = (event: React.KeyboardEvent<HTMLElement>, item: ExplorerItem) => {
    if (event.key === 'Enter' && !isInnerControl(event)) {
      event.preventDefault();
      openItem(item);
    }
  };

  const onViewChange = (mode: ComponentFunctions.ExplorerViewMode) => {
    setViewMode(mode);
    saveViewMode(mode);
  };

  const errorMessage = (error: ErrorType, fallbackKey: string): string => {
    if (error.ErrorCode === 401 || error.ErrorCode === 403) return t('filesExplorer.messages.notAuthorized');
    if (error.ErrorCode === 404) return t('filesExplorer.messages.fileNotFound');
    if (error.ErrorCode === 400 && error.ErrorMessage) return error.ErrorMessage;
    return t(fallbackKey);
  };

  //#region render helpers

  const renderIcon = (item: ExplorerItem, large = false) => {
    const className = mergeClasses(
      large ? styles.tileIcon : styles.icon,
      item.iconKind === 'folder' && styles.iconFolder,
      item.iconKind === 'pdf' && styles.iconPdf,
      item.iconKind === 'spreadsheet' && styles.iconSpreadsheet
    );
    const iconProps = { className, 'aria-hidden': true, 'data-testid': `files-icon-${item.iconKind}` };
    switch (item.iconKind) {
      case 'folder':
        return <Folder24Regular {...iconProps} />;
      case 'pdf':
        return <DocumentPdf24Regular {...iconProps} />;
      case 'spreadsheet':
        return <DocumentTable24Regular {...iconProps} />;
      case 'image':
        return <Image24Regular {...iconProps} />;
      case 'text':
      case 'document':
        return <DocumentText24Regular {...iconProps} />;
      default:
        return <Document24Regular {...iconProps} />;
    }
  };

  const typeLabel = (item: ExplorerItem): string =>
    item.kind === 'folder'
      ? t('filesExplorer.itemTypes.folder')
      : t(`filesExplorer.itemTypes.${item.iconKind}`, { extension: item.extension.toUpperCase() });

  /** System folders: lock with the reason (state never by color alone: icon + tooltip text). */
  const renderLock = (item: ExplorerItem) =>
    item.kind === 'folder' && item.isSystem ? (
      <Tooltip content={t('filesExplorer.systemFolder.tooltip')} relationship="description" withArrow>
        <span
          className={styles.lock}
          tabIndex={0}
          role="img"
          aria-label={t('filesExplorer.systemFolder.label')}
          data-testid="files-system-lock"
        >
          <LockClosed16Regular />
        </span>
      </Tooltip>
    ) : null;

  const renderName = (item: ExplorerItem) => (
    <div className={styles.nameCell}>
      {renderIcon(item)}
      <Tooltip content={item.name} relationship="description" withArrow>
        <Button
          appearance="transparent"
          className={styles.nameButton}
          onClick={() => openItem(item)}
          aria-label={`${typeLabel(item)}: ${item.displayName}`}
          data-testid="files-item-name"
        >
          {item.displayName}
        </Button>
      </Tooltip>
      {renderLock(item)}
    </div>
  );

  const isPending = (item: ExplorerItem, action: StorageFileAction) =>
    props.pendingFile?.path === item.path && props.pendingFile?.action === action;

  const renderActions = (item: ExplorerItem) => {
    if (item.kind === 'folder') {
      return (
        <>
          <Button
            appearance="subtle"
            size="small"
            icon={<FolderOpen24Regular />}
            onClick={() => onNavigate(item.path)}
            aria-label={t('filesExplorer.actions.openNamed', { name: item.displayName })}
            title={t('filesExplorer.actions.open')}
          />
          {!item.isSystem && (
            <>
              <Button
                appearance="subtle"
                size="small"
                icon={<Rename20Regular />}
                onClick={() => openRename(item)}
                disabled={!canManage}
                aria-label={t('filesExplorer.actions.renameNamed', { name: item.displayName })}
                title={t('filesExplorer.actions.rename')}
                data-testid="files-action-rename"
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete20Regular />}
                onClick={() => setConfirmDelete(item)}
                disabled={!canManage}
                aria-label={t('filesExplorer.actions.deleteNamed', { name: item.displayName })}
                title={t('filesExplorer.actions.deleteFolder')}
                data-testid="files-action-delete-folder"
              />
            </>
          )}
        </>
      );
    }
    const openAction = getOpenFileAction(item.extension);
    return (
      <>
        <Button
          appearance="subtle"
          size="small"
          icon={<Eye20Regular />}
          onClick={() => previewItem(item)}
          aria-label={t('filesExplorer.actions.previewNamed', { name: item.name })}
          title={t('filesExplorer.actions.preview')}
          data-testid="files-action-preview"
        />
        <Button
          appearance="subtle"
          size="small"
          icon={isPending(item, openAction) ? <Spinner size="extra-tiny" /> : <Open20Regular />}
          onClick={() => runFileAction(item, openAction)}
          disabled={!!props.pendingFile}
          aria-label={t('filesExplorer.actions.openNamed', { name: item.name })}
          title={openAction === 'open' ? t('filesExplorer.actions.openInNewTab') : t('filesExplorer.actions.openDownloads')}
          data-testid="files-action-open"
        />
        <Button
          appearance="subtle"
          size="small"
          icon={isPending(item, 'download') ? <Spinner size="extra-tiny" /> : <ArrowDownload20Regular />}
          onClick={() => runFileAction(item, 'download')}
          disabled={!!props.pendingFile}
          aria-label={t('filesExplorer.actions.downloadNamed', { name: item.name })}
          title={t('filesExplorer.actions.download')}
          data-testid="files-action-download"
        />
        <Button
          appearance="subtle"
          size="small"
          icon={<Delete20Regular />}
          onClick={() => setConfirmDelete(item)}
          disabled={!canManage}
          aria-label={t('filesExplorer.actions.deleteNamed', { name: item.name })}
          title={t('filesExplorer.actions.delete')}
          data-testid="files-action-delete-file"
        />
      </>
    );
  };

  const formatDate = (value: string | undefined) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '—');

  const columns: TableColumnDefinition<ExplorerItem>[] = [
    createTableColumn<ExplorerItem>({
      columnId: 'name',
      renderHeaderCell: () => t('filesExplorer.columns.name'),
      renderCell: (item) => renderName(item),
    }),
    createTableColumn<ExplorerItem>({
      columnId: 'modified',
      renderHeaderCell: () => t('filesExplorer.columns.modified'),
      renderCell: (item) => <span className={styles.secondaryText}>{formatDate(item.lastModified)}</span>,
    }),
    createTableColumn<ExplorerItem>({
      columnId: 'size',
      renderHeaderCell: () => t('filesExplorer.columns.size'),
      renderCell: (item) => (
        <span className={styles.secondaryText}>{item.kind === 'file' ? formatFileSize(item.size) : '—'}</span>
      ),
    }),
    createTableColumn<ExplorerItem>({
      columnId: 'actions',
      renderHeaderCell: () => t('filesExplorer.columns.actions'),
      renderCell: (item) => renderActions(item),
    }),
  ];

  const columnSizing = {
    name: { minWidth: 240, idealWidth: 480 },
    modified: { minWidth: 140, idealWidth: 160 },
    size: { minWidth: 90, idealWidth: 100 },
    actions: { minWidth: 128, idealWidth: 140 },
  };

  const cellClass = (columnId: string | number) =>
    columnId === 'size' ? styles.numericCell : columnId === 'actions' ? styles.actionsCell : undefined;

  const renderList = () => (
    <div className={styles.surface}>
      <DataGrid
        items={visibleItems}
        columns={columns}
        getRowId={(item: ExplorerItem) => item.key}
        focusMode="composite"
        columnSizingOptions={columnSizing}
        aria-label={t('filesExplorer.gridLabel', { folder: currentFolder.displayName })}
        data-testid="files-list"
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell, columnId }) => (
              <DataGridHeaderCell className={mergeClasses(styles.headerCell, cellClass(columnId))}>
                {renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<ExplorerItem>>
          {({ item, rowId }) => (
            <DataGridRow<ExplorerItem>
              key={rowId}
              className={styles.row}
              onDoubleClick={() => openItem(item)}
              onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => onItemKeyDown(event, item)}
              data-testid={`files-row-${item.kind}`}
            >
              {({ renderCell, columnId }) => (
                <DataGridCell className={cellClass(columnId)}>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );

  /** Grid view: OneDrive-like tiles (preview area with the type icon, name below, meta line, actions on hover/focus). */
  const renderGrid = () => (
    <ul className={styles.grid} aria-label={t('filesExplorer.gridLabel', { folder: currentFolder.displayName })} data-testid="files-grid">
      {visibleItems.map((item) => (
        <li key={item.key} className={styles.tileItem}>
          <div
            className={mergeClasses(styles.tile, item.kind === 'folder' && styles.tileFolder)}
            role="group"
            tabIndex={0}
            aria-label={`${typeLabel(item)}: ${item.displayName}`}
            onDoubleClick={() => openItem(item)}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onItemKeyDown(event, item)}
            data-testid={`files-card-${item.kind}`}
          >
            <div className={mergeClasses(styles.tilePreview, item.kind === 'folder' && styles.tilePreviewFolder)}>
              {renderIcon(item, true)}
              {item.kind === 'file' && item.extension && (
                <span className={styles.tileBadge} aria-hidden="true">
                  {item.extension.toUpperCase()}
                </span>
              )}
            </div>
            <div className={styles.tileActions} data-tile-actions="">
              {renderActions(item)}
            </div>
            <div className={styles.tileBody}>
              <div className={styles.tileNameRow}>
                <Tooltip content={item.name} relationship="description" withArrow>
                  <Button
                    appearance="transparent"
                    className={styles.tileName}
                    onClick={() => openItem(item)}
                    aria-label={`${typeLabel(item)}: ${item.displayName}`}
                    data-testid="files-item-name"
                  >
                    <span className={styles.tileNameText}>{item.displayName}</span>
                  </Button>
                </Tooltip>
                {renderLock(item)}
              </div>
              <div className={styles.tileMeta}>
                <span>{item.kind === 'folder' ? typeLabel(item) : formatDate(item.lastModified)}</span>
                {item.kind === 'file' && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{formatFileSize(item.size)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  const renderSkeleton = () => (
    <div className={styles.surface}>
      <Skeleton aria-label={t('filesExplorer.messages.loading')} className={styles.skeleton} data-testid="files-skeleton">
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={index} className={styles.skeletonRow}>
            <SkeletonItem shape="square" size={24} />
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
            <SkeletonItem size={16} />
          </div>
        ))}
      </Skeleton>
    </div>
  );

  const renderEmpty = () => {
    if (search.trim() && items.length > 0) {
      return (
        <div className={styles.emptyState} data-testid="files-no-results">
          <FolderOpen24Regular className={styles.emptyIcon} aria-hidden />
          <span className={styles.emptyTitle}>{t('filesExplorer.empty.noResultsTitle', { search: search.trim() })}</span>
          <span className={styles.emptyText}>{t('filesExplorer.empty.noResultsHelp')}</span>
          <Button appearance="primary" icon={<Dismiss20Regular />} onClick={() => setSearch('')}>
            {t('filesExplorer.actions.clearSearch')}
          </Button>
        </div>
      );
    }
    return (
      <div className={styles.emptyState} data-testid="files-empty">
        <FolderOpen24Regular className={styles.emptyIcon} aria-hidden />
        <span className={styles.emptyTitle}>{t('filesExplorer.empty.title')}</span>
        <span className={styles.emptyText}>{t('filesExplorer.empty.help')}</span>
        {path ? (
          <Button appearance="primary" icon={<ArrowUp20Regular />} onClick={() => onNavigate(getParentPath(path))}>
            {t('filesExplorer.actions.up')}
          </Button>
        ) : (
          <Button appearance="primary" icon={<ArrowClockwise20Regular />} onClick={reload}>
            {t('filesExplorer.actions.refresh')}
          </Button>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (isBusy && items.length === 0) return renderSkeleton();
    if (props.error && items.length === 0) return null;
    if (visibleItems.length === 0) return props.isLoaded ? renderEmpty() : renderSkeleton();
    return viewMode === 'grid' ? renderGrid() : renderList();
  };

  /** Disabled buttons keep focus (disabledFocusable) so the tooltip explaining why is reachable by keyboard. */
  const renderUploadButton = () => (
    <Tooltip
      content={canUpload ? t('filesExplorer.upload.tooltip') : t('filesExplorer.upload.disabledRoot')}
      relationship="description"
      withArrow
    >
      <ToolbarButton
        appearance="primary"
        icon={<ArrowUpload20Regular />}
        onClick={() => uploadInputRef.current?.click()}
        disabledFocusable={!canUpload}
        data-testid="files-upload"
      >
        {t('filesExplorer.actions.upload')}
      </ToolbarButton>
    </Tooltip>
  );

  const renderNewFolderButton = () => (
    <Tooltip
      content={canCreateFolder ? t('filesExplorer.actions.newFolder') : t('filesExplorer.folderDialog.disabledEntity')}
      relationship="description"
      withArrow
    >
      <ToolbarButton
        icon={<FolderAdd20Regular />}
        onClick={() => setFolderDialog({ mode: 'create' })}
        disabledFocusable={!canCreateFolder}
        data-testid="files-new-folder"
      >
        {t('filesExplorer.actions.newFolder')}
      </ToolbarButton>
    </Tooltip>
  );

  //#endregion

  return (
    <section
      className={mergeClasses(styles.root, isDraggingFiles && styles.rootDragging)}
      aria-busy={isBusy}
      data-testid="files-explorer"
      {...dropTargetProps}
    >
      <Toaster toasterId={toasterId} position="bottom-start" />
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
            <Breadcrumb className={styles.breadcrumb} aria-label={t('filesExplorer.breadcrumbLabel')} data-testid="files-breadcrumb">
              {breadcrumbs.map((crumb, index) => {
                const isCurrent = index === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={crumb.key}>
                    <BreadcrumbItem>
                      <Tooltip content={crumb.name} relationship="description" withArrow>
                        <BreadcrumbButton
                          current={isCurrent}
                          onClick={isCurrent ? undefined : () => onNavigate(crumb.path)}
                          data-testid={`files-breadcrumb-${index}`}
                        >
                          {crumb.displayName}
                        </BreadcrumbButton>
                      </Tooltip>
                    </BreadcrumbItem>
                    {!isCurrent && <BreadcrumbDivider />}
                  </React.Fragment>
                );
              })}
            </Breadcrumb>
          <h2 className={styles.folderTitle} title={currentFolder.name}>
            {currentFolder.displayName}
          </h2>
          {!isBusy && !props.error && (
            <span className={styles.summary} data-testid="files-summary">
              {t('filesExplorer.summary', { folders: folderCount, files: fileCount })}
              {props.continuationToken ? ` · ${t('filesExplorer.moreAvailable')}` : ''}
            </span>
          )}
        </div>

        <div className={styles.toolbarRow}>
          <SearchBox
            className={styles.search}
            value={search}
            onChange={(_, data) => setSearch(data.value)}
            placeholder={t('filesExplorer.searchPlaceholder')}
            aria-label={t('filesExplorer.searchPlaceholder')}
            data-testid="files-search"
          />
          <Toolbar
            className={styles.toolbar}
            aria-label={t('filesExplorer.toolbarLabel')}
            checkedValues={{ view: [viewMode] }}
            onCheckedValueChange={(_, data) => {
              const mode = data.checkedItems[0];
              if (data.name === 'view' && (mode === 'list' || mode === 'grid')) onViewChange(mode);
            }}
          >
            {renderUploadButton()}
            {renderNewFolderButton()}
            {canRenameCurrent && (
              <ToolbarButton
                icon={<Rename20Regular />}
                onClick={() =>
                  openRename({ path, name: currentFolder.name, displayName: currentFolder.displayName })
                }
                aria-label={t('filesExplorer.actions.renameCurrent')}
                title={t('filesExplorer.actions.renameCurrent')}
                data-testid="files-rename-current"
              />
            )}
            <ToolbarDivider />
            <ToolbarButton
              icon={<ArrowUp20Regular />}
              onClick={() => onNavigate(getParentPath(path))}
              disabled={!path}
              aria-label={t('filesExplorer.actions.up')}
              title={t('filesExplorer.actions.up')}
            />
            <ToolbarButton
              icon={isBusy ? <Spinner size="extra-tiny" /> : <ArrowClockwise20Regular />}
              onClick={reload}
              disabled={isBusy}
              aria-label={t('filesExplorer.actions.refresh')}
              title={t('filesExplorer.actions.refresh')}
              data-testid="files-refresh"
            />
            <ToolbarDivider />
            <ToolbarRadioGroup aria-label={t('filesExplorer.viewLabel')}>
              <ToolbarRadioButton
                name="view"
                value="list"
                appearance="subtle"
                icon={<TextBulletListLtr20Regular />}
                aria-label={t('filesExplorer.views.list')}
                title={t('filesExplorer.views.list')}
                data-testid="files-view-list"
              >
                {t('filesExplorer.views.list')}
              </ToolbarRadioButton>
              <ToolbarRadioButton
                name="view"
                value="grid"
                appearance="subtle"
                icon={<Grid20Regular />}
                aria-label={t('filesExplorer.views.grid')}
                title={t('filesExplorer.views.grid')}
                data-testid="files-view-grid"
              >
                {t('filesExplorer.views.grid')}
              </ToolbarRadioButton>
            </ToolbarRadioGroup>
          </Toolbar>
        </div>
      </div>

      {props.error && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="files-error">
          <MessageBarBody>
            <MessageBarTitle>{t('filesExplorer.messages.loadErrorTitle')}</MessageBarTitle>
            {errorMessage(props.error, 'filesExplorer.messages.loadError')}
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="transparent" size="small" onClick={retry}>
              {t('filesExplorer.actions.retry')}
            </Button>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {props.fileError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="files-file-error">
          <MessageBarBody>
            <MessageBarTitle>
              {t(
                props.fileError.action === 'download'
                  ? 'filesExplorer.messages.downloadErrorTitle'
                  : 'filesExplorer.messages.openErrorTitle'
              )}
            </MessageBarTitle>
            {errorMessage(props.fileError.error, 'filesExplorer.messages.fileError')}
          </MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss20Regular />}
                aria-label={t('filesExplorer.actions.dismiss')}
                onClick={props.ClearFileError}
              />
            }
          />
        </GuideMessageBar>
      )}

      {renderContent()}

      <FilePreview open={!!previewFile} file={previewFile} onDismiss={() => setPreviewFile(undefined)} />

      {isDraggingFiles && (
        <div className={styles.dropHint} role="status" data-testid="files-drop-hint">
          <ArrowUpload20Regular aria-hidden />
          {t('filesExplorer.upload.dropHere', { folder: currentFolder.displayName })}
        </div>
      )}

      <input
        ref={uploadInputRef}
        type="file"
        multiple
        hidden
        accept={buildFolderAcceptAttribute(capabilities.allowedExtensions)}
        onChange={onUploadInputChange}
        data-testid="files-upload-input"
      />

      <ExplorerUploadPanel
        open={uploadOpen}
        folderLabel={currentFolder.displayName}
        batch={uploadBatch}
        allowedExtensions={capabilities.allowedExtensions}
        uploadFile={uploadFile}
        onUploaded={onUploaded}
        isRegistrable={isRegistrable}
        registerFile={registerUploadedFile}
        onDismiss={() => setUploadOpen(false)}
      />

      <FolderNameDialog
        open={!!folderDialog}
        mode={folderDialog?.mode ?? 'create'}
        initialName={folderDialog?.mode === 'rename' ? folderDialog.name : undefined}
        folderLabel={folderDialog?.mode === 'rename' ? folderDialog.displayName : currentFolder.displayName}
        siblingNames={folderDialog?.mode === 'rename' && folderDialog.path === path ? [] : siblingFolderNames}
        onSubmit={submitFolderDialog}
        onDismiss={() => setFolderDialog(undefined)}
        describeError={describeFolderError}
      />

      <ConfirmActionDialog
        open={!!confirmDelete}
        testId="files-delete-dialog"
        title={t(confirmDelete?.kind === 'folder' ? 'filesExplorer.deleteDialog.folderTitle' : 'filesExplorer.deleteDialog.fileTitle')}
        message={t(
          confirmDelete?.kind === 'folder' ? 'filesExplorer.deleteDialog.folderMessage' : 'filesExplorer.deleteDialog.fileMessage',
          { name: confirmDelete?.kind === 'folder' ? confirmDelete?.displayName : confirmDelete?.name }
        )}
        warning={t(
          confirmDelete?.kind === 'folder' ? 'filesExplorer.deleteDialog.folderWarning' : 'filesExplorer.deleteDialog.fileWarning'
        )}
        confirmLabel={t('filesExplorer.actions.delete')}
        errorTitle={t('filesExplorer.deleteDialog.errorTitle')}
        onConfirm={confirmDeleteItem}
        onDismiss={() => setConfirmDelete(undefined)}
        describeError={describeDeleteError}
      />

      {props.continuationToken && items.length > 0 && !isBusy && (
        <div className={styles.loadMore}>
          <Button
            appearance="secondary"
            onClick={loadMore}
            disabled={props.isLoadingMore}
            icon={props.isLoadingMore ? <Spinner size="extra-tiny" /> : undefined}
            data-testid="files-load-more"
          >
            {t('filesExplorer.actions.loadMore')}
          </Button>
        </div>
      )}
    </section>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(FilesExplorerComponent as React.ComponentType<ComponentFunctions.Props>);
